import { useEffect, useRef } from "react";

/**
 * useElementTilt
 *
 * Cursor-tracked 3D tilt for a single element. Attach the returned ref to
 * the element you want to tilt. CSS vars written on the element:
 *   --tilt-x: -1..1 (pointer X relative to element centre)
 *   --tilt-y: -1..1 (pointer Y relative to element centre)
 *   --tilt-active: 0 when pointer is outside, 1 when inside
 *
 * CSS can then do e.g.
 *   transform: perspective(800px)
 *              rotateY(calc(var(--tilt-x) * 8deg))
 *              rotateX(calc(var(--tilt-y) * -8deg));
 *
 * Disabled for prefers-reduced-motion and coarse (touch) pointers.
 */
export default function useElementTilt({ strength = 1 } = {}) {
  const ref = useRef(null);

  useEffect(() => {
    const el = ref.current;
    if (!el || typeof window === "undefined") return;

    const prefersReduced =
      window.matchMedia &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const coarsePointer =
      window.matchMedia && window.matchMedia("(pointer: coarse)").matches;
    if (prefersReduced || coarsePointer) return;

    let ticking = false;
    let lastEvent = null;

    const update = () => {
      ticking = false;
      if (!lastEvent) return;
      const rect = el.getBoundingClientRect();
      const cx = rect.left + rect.width / 2;
      const cy = rect.top + rect.height / 2;
      const tx = ((lastEvent.clientX - cx) / (rect.width / 2)) * strength;
      const ty = ((lastEvent.clientY - cy) / (rect.height / 2)) * strength;
      const clamp = (v) => Math.max(-1, Math.min(1, v));
      el.style.setProperty("--tilt-x", clamp(tx).toFixed(3));
      el.style.setProperty("--tilt-y", clamp(ty).toFixed(3));
      // Local pointer position (0..1 within the element) for cursor spotlight.
      const sx = (lastEvent.clientX - rect.left) / rect.width;
      const sy = (lastEvent.clientY - rect.top) / rect.height;
      el.style.setProperty(
        "--spot-x",
        `${Math.max(0, Math.min(1, sx)) * 100}%`
      );
      el.style.setProperty(
        "--spot-y",
        `${Math.max(0, Math.min(1, sy)) * 100}%`
      );
    };

    const onMove = (e) => {
      lastEvent = e;
      if (!ticking) {
        ticking = true;
        window.requestAnimationFrame(update);
      }
    };

    const onEnter = () => {
      el.style.setProperty("--tilt-active", "1");
    };
    const onLeave = () => {
      el.style.setProperty("--tilt-active", "0");
      el.style.setProperty("--tilt-x", "0");
      el.style.setProperty("--tilt-y", "0");
    };

    el.addEventListener("mousemove", onMove, { passive: true });
    el.addEventListener("mouseenter", onEnter, { passive: true });
    el.addEventListener("mouseleave", onLeave, { passive: true });
    return () => {
      el.removeEventListener("mousemove", onMove);
      el.removeEventListener("mouseenter", onEnter);
      el.removeEventListener("mouseleave", onLeave);
    };
  }, [strength]);

  return ref;
}
